// リレー転送の E2E 暗号化（M4）。
// AES-256-GCM。鍵 32 bytes・IV 12 bytes をランダム生成し、GCM タグ（16 bytes）は
// 暗号文末尾に連結する（WebCrypto の encrypt 出力がそのままこの形式）。
// 鍵・IV はリレーへ一切送らず、6桁コードと一緒に QR / 手入力文字列で受け渡す。
//
// フォーマットは Unity 側 FmAesGcm.cs と厳密に一致させること。

/** 転送の秘密情報（6桁コード + 鍵 + IV）。 */
export interface TransferSecret {
  code: string;
  keyB64: string;
  ivB64: string;
}

/** 暗号化結果。 */
export interface EncryptedPayload {
  /** 暗号文 + GCMタグ（末尾16bytes）。 */
  cipherBytes: ArrayBuffer;
  /** 鍵（base64url、32bytes 分 = 43文字）。 */
  keyB64: string;
  /** IV（base64url、12bytes 分 = 16文字）。 */
  ivB64: string;
}

/** バイト列 → base64url（パディングなし）。 */
export function toBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** base64url → バイト列。不正文字列は例外。 */
export function fromBase64Url(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "==".slice(0, (4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** ランダム鍵・IV で暗号化する。 */
export async function encryptForTransfer(plain: ArrayBuffer): Promise<EncryptedPayload> {
  const keyBytes = crypto.getRandomValues(new Uint8Array(32));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await crypto.subtle.importKey("raw", keyBytes, { name: "AES-GCM" }, false, ["encrypt"]);
  const cipherBytes = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plain);
  return { cipherBytes, keyB64: toBase64Url(keyBytes), ivB64: toBase64Url(iv) };
}

/**
 * 指定の鍵・IV（base64url）で暗号化する（受信箱への投函用）。
 * @throws Error 鍵/IV の長さ不正
 */
export async function encryptWithKey(plain: ArrayBuffer, keyB64: string, ivB64: string): Promise<ArrayBuffer> {
  const keyBytes = fromBase64Url(keyB64);
  const iv = fromBase64Url(ivB64);
  if (keyBytes.length !== 32) throw new Error("鍵の長さが不正です（32バイトの base64url が必要）。");
  if (iv.length !== 12) throw new Error("IV の長さが不正です（12バイトの base64url が必要）。");
  const key = await crypto.subtle.importKey("raw", keyBytes, { name: "AES-GCM" }, false, ["encrypt"]);
  return crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plain);
}

/**
 * 復号する。
 * @throws Error 鍵/IV の長さ不正、タグ検証失敗（コード違い・破損）
 */
export async function decryptFromTransfer(
  cipherBytes: ArrayBuffer,
  keyB64: string,
  ivB64: string,
): Promise<ArrayBuffer> {
  const keyBytes = fromBase64Url(keyB64);
  const iv = fromBase64Url(ivB64);
  if (keyBytes.length !== 32) throw new Error("鍵の長さが不正です（32バイトの base64url が必要）。");
  if (iv.length !== 12) throw new Error("IV の長さが不正です（12バイトの base64url が必要）。");
  const key = await crypto.subtle.importKey("raw", keyBytes, { name: "AES-GCM" }, false, ["decrypt"]);
  try {
    return await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, cipherBytes);
  } catch {
    throw new Error("復号に失敗しました。コードまたは鍵が正しくありません。");
  }
}

/** 手入力・コピペ用の秘密情報文字列 `code:key:iv`。 */
export function formatSecret(code: string, keyB64: string, ivB64: string): string {
  return `${code}:${keyB64}:${ivB64}`;
}

/**
 * QR に載せる URL を組み立てる: `https://<app>/#<code>:<key>:<iv>`。
 * ハッシュに載せるので鍵はサーバーのアクセスログにも残らない。
 */
export function buildQrPayload(appOrigin: string, code: string, keyB64: string, ivB64: string): string {
  return `${appOrigin}/#${formatSecret(code, keyB64, ivB64)}`;
}

const SECRET_RE = /^(\d{6}):([A-Za-z0-9_-]{43}):([A-Za-z0-9_-]{16})$/;
const SEND_RE = /^send:(\d{6}):([A-Za-z0-9_-]{43}):([A-Za-z0-9_-]{16})$/;
const SENDCLIP_RE = /^sendclip:(\d{6}):([A-Za-z0-9_-]{43}):([A-Za-z0-9_-]{16})$/;
const RECVCLIP_RE = /^recvclip:(\d{6}):([A-Za-z0-9_-]{43}):([A-Za-z0-9_-]{16})$/;

function afterHash(input: string): string {
  const s = input.trim();
  const i = s.indexOf("#");
  return i >= 0 ? s.slice(i + 1) : s;
}

/** QR ペイロード or 手入力文字列（`code:key:iv`、URL 形式も可）を解析する。不正なら null。 */
export function parseTransferSecret(input: string): TransferSecret | null {
  const m = SECRET_RE.exec(afterHash(input));
  if (!m) return null;
  return { code: m[1], keyB64: m[2], ivB64: m[3] };
}

/**
 * 受信待ちモード起動用ペイロード（Unity が表示する QR）を解析する。不正なら null。
 * 形式: `#send:<6桁>:<key43字>:<iv16字>`
 */
export function parseSendTarget(input: string): TransferSecret | null {
  const m = SEND_RE.exec(afterHash(input));
  if (!m) return null;
  return { code: m[1], keyB64: m[2], ivB64: m[3] };
}

/**
 * 表情クリップパック送信待ちモード起動用ペイロードを解析する。不正なら null。
 * 形式: `#sendclip:<6桁>:<key43字>:<iv16字>`
 */
export function parseClipPackSendTarget(input: string): TransferSecret | null {
  const m = SENDCLIP_RE.exec(afterHash(input));
  if (!m) return null;
  return { code: m[1], keyB64: m[2], ivB64: m[3] };
}

/**
 * 表情クリップパック受信用ペイロードを解析する。不正なら null。
 * 形式: `#recvclip:<6桁>:<key43字>:<iv16字>`
 */
export function parseClipPackRecvSecret(input: string): TransferSecret | null {
  const m = RECVCLIP_RE.exec(afterHash(input));
  if (!m) return null;
  return { code: m[1], keyB64: m[2], ivB64: m[3] };
}

/** QR カメラ読み取りで得た送信待ちペイロード。 */
export type ScannedSendTarget = { kind: "fmavatar" | "clippack"; secret: TransferSecret };

/**
 * QR の生データ（URL 形式 `https://.../#send:...` でも、生文字列 `send:...` でも可）を解析し、
 * `.fmavatar` 送信待ち（send）か、クリップパック送信待ち（sendclip）かを判定する。不正なら null。
 */
export function parseScannedQr(text: string): ScannedSendTarget | null {
  const send = parseSendTarget(text);
  if (send) return { kind: "fmavatar", secret: send };
  const clip = parseClipPackSendTarget(text);
  if (clip) return { kind: "clippack", secret: clip };
  return null;
}
